import { useRef, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useRouter } from 'next/router'
import { RemoteParticipant, Room, RoomEvent } from 'livekit-client'

import style from '@/styles/waitingRoom.module.css'

import { checkIsRealParticipant, getVisibleParticipants } from '@/src/helpers/visibleParticipants'

import { useAppSelector } from '@/src/store/hooks'
import { selectBranding } from '@/src/store/slices/meetData'

interface IWaitingRoomProps {
  room: Room
  /** Возвращает гостя на форму входа без ухода на сайт компании. */
  onLeaveWaitingRoom: () => void
}

export function WaitingRoom({ room, onLeaveWaitingRoom }: IWaitingRoomProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const brandingData = useAppSelector(selectBranding)
  const { primary_color, text_primary_color } = brandingData

  const sessionStorageBranding = JSON.parse(sessionStorage.getItem('brandingData') || '{}')
  const brandingDataParse =
    Object.keys(brandingData).length > 0 ? brandingData : sessionStorageBranding

  const [isPlaying, setIsPlaying] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  const handlePlayButtonClick = async () => {
    setIsPlaying(true)
    if (videoRef.current) {
      await videoRef.current.play()
    }
  }

  const handleVideoPause = () => {
    setIsPlaying(false)
  }

  const handleVideoPlay = () => {
    setIsPlaying(true)
  }

  useEffect(() => {
    router.replace({ query: { ...router.query, inWaitingRoom: 'true' } })

    const video = videoRef?.current

    if (video) {
      video.addEventListener('pause', handleVideoPause)
      video.addEventListener('ended', handleVideoPause)
      video.addEventListener('play', handleVideoPlay)

      return () => {
        video.removeEventListener('pause', handleVideoPause)
        video.removeEventListener('ended', handleVideoPause)
        video.removeEventListener('play', handleVideoPlay)
      }
    }
  }, [])

  useEffect(() => {
    const handleParticipantDisconnected = (participant: RemoteParticipant) => {
      if (participant) {
        const isRealParticipant = checkIsRealParticipant(participant)

        if (isRealParticipant) {
          const visibleParticipants = getVisibleParticipants(room?.remoteParticipants)

          if (visibleParticipants?.length === 0) {
            router?.reload()
          }
        }
      }
    }

    room?.on(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)

    return () => {
      room?.off(RoomEvent.ParticipantDisconnected, handleParticipantDisconnected)
    }
  }, [router, room, room?.remoteParticipants])

  /**
   * Возвращает на форму входа по кнопке «Назад».
   *
   * Раньше здесь был location.replace той же ссылки: страница уходила,
   * beforeunload снимал участника, а ActiveRoom воспринимал это как конец
   * звонка и открывал сайт компании.
   */
  const handleClick = () => {
    onLeaveWaitingRoom()
  }

  return (
    <div className={style.bodyWaitingRoom}>
      <div className="logo">
        <img src={brandingDataParse.logo} alt="Proofix Meet" />
      </div>

      <div className={style.title} style={{ color: brandingDataParse.text_primary_color }}>
        <h1>{brandingDataParse.title_waiting}</h1>
        <p>{brandingDataParse.description_waiting}</p>
      </div>

      {brandingDataParse?.img_waiting && (
        <div className={style.card}>
          <img src={brandingDataParse.img_waiting} alt="Proofix Meet" />
        </div>
      )}

      <div className={style.border}>
        {/*loader*/}
        <span></span>
      </div>

      <div className={style.box_loader}>
        <div className={style.loader}></div>

        <div>
          <p style={{ color: text_primary_color }}>{t('pleaseWait')}</p>
          <button
            type="button"
            style={{ width: '100%' }}
            className="lk-button"
            onClick={handleClick}
          >
            {t('back')}
          </button>
        </div>
      </div>

      <div className={style.border}>
        <span></span>
      </div>

      {brandingDataParse?.video_waiting && (
        <div>
          <div className={style.title} style={{ color: text_primary_color }}>
            <p>Пока вы ждете начала встречи, можете посмотреть наш промо-ролик:</p>
          </div>

          <div className={style.video_box}>
            {!isPlaying && (
              <div className={style.video_box_play}>
                <div className={style.button_one} onClick={handlePlayButtonClick}>
                  <svg
                    className={style.playButton}
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M27.5 10.7519L97.5 55.8647C99 56.3659 100 58.3709 100 59.8747C100 61.3784 99 62.8822 97.5 63.8847L27.5 108.997C27 109.499 26 110 25 110C24 110 23.5 110 22.5 109.499C21 108.496 20 106.992 20 104.987V14.7619C20 12.7569 21 11.2531 22.5 10.7519C24 9.74937 26 9.74937 27.5 10.7519ZM86 59.8747L30 23.7845V95.9649L86 59.8747Z"
                      fill={primary_color}
                    />
                    <mask
                      id="mask0_825_15195"
                      style={{ maskType: 'luminance' }}
                      maskUnits="userSpaceOnUse"
                      x="20"
                      y="10"
                      width="80"
                      height="100"
                    >
                      <path
                        d="M97.5 55.8647L27.5 10.7519C26 9.74937 24 9.74937 22.5 10.7519C21 11.2531 20 12.7569 20 14.7619V104.987C20 106.992 21 108.496 22.5 109.499C23.5 110 24 110 25 110C26 110 27 109.499 27.5 108.997L97.5 63.8847C99 62.8822 100 61.3784 100 59.8747C100 58.3709 99 56.3659 97.5 55.8647Z"
                        fill="white"
                      />
                    </mask>
                    <g mask="url(#mask0_825_15195)">
                      <rect width="120" height="120" fill={primary_color} fillOpacity="0.6" />
                    </g>
                  </svg>
                </div>
              </div>
            )}

            <video ref={videoRef} controls style={{ width: '100%' }}>
              <source src={brandingDataParse.video_waiting} type="video/mp4" />
            </video>
          </div>
        </div>
      )}
    </div>
  )
}
